import React, { useEffect } from 'react';
import { ArrowRight, Shield, Users, FileText, Activity } from 'lucide-react';
import { Link } from 'react-router-dom';

const HowItWorks = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="cy-body">
      <section className="page-hero">
        <span className="section-label">How it works</span>
        <h1>Five steps from <span>contract</span> to <span>revenue</span>.</h1>
        <p className="page-sub">
          A fully automated pipeline that reads your VBC contracts, computes patient outcomes, and tells you exactly what you're owed — with the operational workflows to capture what you're missing.
        </p>
      </section>

      <div className="cy-steps">
        {/* STEP 1 */}
        <div className="step reveal">
          <div className="step-left">
            <div className="step-num">01</div>
            <div className="step-tag">Step 1 — Ingest</div>
            <h3>Upload your VBC contract</h3>
            <p>Drop any value-based care contract — PDF or Word — into CareYield. Our AI reads the full document and extracts every clause that affects your revenue: quality measures, performance thresholds, tiered payout structures, exclusion criteria, risk adjustment methodology, and attribution rules.</p>
            <p>The output is a structured, executable specification that your team can validate before any calculation runs. Every extracted element links back to its contract clause reference.</p>
            <div className="step-detail">Supports multi-payer environments. Process contracts from UnitedHealthcare, Aetna, BCBS, Cigna, Humana, and regional plans.</div>
          </div>
          <div className="step-visual sv-gold">
            <div className="vis-title">What we extract</div>
            <div className="cl"><div className="cl-dot" style={{ background: 'var(--cy-gold)' }} /><span className="cl-label">Measure</span><span className="cl-val">Diabetes Control — HbA1c &lt; 7.0</span></div>
            <div className="cl"><div className="cl-dot" style={{ background: 'var(--cy-teal)' }} /><span className="cl-label">Population</span><span className="cl-val">Type 2 diabetes (ICD E11.x)</span></div>
            <div className="cl"><div className="cl-dot" style={{ background: 'var(--cy-purple)' }} /><span className="cl-label">Payout</span><span className="cl-val">Tiered: $50 PMPM (1.0x / 1.25x / 1.5x)</span></div>
            <div className="cl"><div className="cl-dot" style={{ background: 'var(--cy-red)' }} /><span className="cl-label">Exclusions</span><span className="cl-val">Hospice, ESRD, age &lt; 18, enroll &lt; 90d</span></div>
            <div className="cl"><div className="cl-dot" style={{ background: 'var(--cy-gold)' }} /><span className="cl-label">Attribution</span><span className="cl-val">PCP plurality — 24mo lookback</span></div>
            <div className="cl"><div className="cl-dot" style={{ background: 'var(--cy-txm)' }} /><span className="cl-label">Risk adj.</span><span className="cl-val">HCC-CMS v28 (2025)</span></div>
            <div className="json-preview">
              {'{'}<br />
              &nbsp;&nbsp;<span className="jk">"measure_id"</span>: <span className="js">"DM-CTRL-01"</span>,<br />
              &nbsp;&nbsp;<span className="jk">"tiers"</span>: [<br />
              &nbsp;&nbsp;&nbsp;&nbsp;{'{ '}<span className="jk">"min"</span>: <span className="jn">0.75</span>, <span className="jk">"mult"</span>: <span className="jn">1.0</span> {'}'},<br />
              &nbsp;&nbsp;&nbsp;&nbsp;{'{ '}<span className="jk">"min"</span>: <span className="jn">0.85</span>, <span className="jk">"mult"</span>: <span className="jn">1.25</span> {'}'},<br />
              &nbsp;&nbsp;&nbsp;&nbsp;{'{ '}<span className="jk">"min"</span>: <span className="jn">0.95</span>, <span className="jk">"mult"</span>: <span className="jn">1.50</span> {'}'}<br />
              &nbsp;&nbsp;]<br />
              {'}'}
            </div>
          </div>
        </div>

        {/* STEP 2 */}
        <div className="step reveal">
          <div className="step-visual sv-teal" style={{ order: -1 }}>
            <div className="vis-title">EMR integration pipeline</div>
            <div className="flow-steps">
              <div className="fs"><div className="fs-icon fi-teal">+</div><div className="fs-text"><strong>Epic FHIR R4</strong> — Patient, Observation, Condition, Encounter, Medication</div></div>
              <div className="fs"><div className="fs-icon fi-gold">↓</div><div className="fs-text"><strong>Identity matching</strong> — Probabilistic matching across data sources (96.2% match rate)</div></div>
              <div className="fs"><div className="fs-icon fi-teal">↓</div><div className="fs-text"><strong>Normalization</strong> — Unit conversion, deduplication, ICD validation, null handling</div></div>
              <div className="fs"><div className="fs-icon fi-purple">↓</div><div className="fs-text"><strong>Cohort builder</strong> — Measure-specific patient populations with exclusions applied</div></div>
              <div className="fs"><div className="fs-icon fi-gold">✓</div><div className="fs-text"><strong>Analytics-ready</strong> — Clean, attributed, measure-eligible patient cohorts</div></div>
            </div>
          </div>
          <div className="step-left">
            <div className="step-num">02</div>
            <div className="step-tag">Step 2 — Connect</div>
            <h3>Integrate your EMR data</h3>
            <p>CareYield connects to your electronic medical record system via standard FHIR R4 APIs. We pull the clinical data that matters: patient demographics, lab results, diagnoses, admissions, medications, and insurance coverage.</p>
            <p>Raw data is normalized before any calculation runs. Lab units are standardized, duplicate records are merged through probabilistic identity matching, invalid codes are flagged, and patients are deduplicated across data sources.</p>
            <div className="step-detail">Full and incremental sync modes. Daily bulk loads plus 4-hour delta pulls ensure your data is never more than a few hours stale.</div>
          </div>
        </div>

        {/* STEP 3 */}
        <div className="step reveal">
          <div className="step-left">
            <div className="step-num">03</div>
            <div className="step-tag">Step 3 — Calculate</div>
            <h3>Compute outcomes against your contracts</h3>
            <p>For each contract measure, CareYield identifies the eligible patient cohort, applies all exclusion rules, attributes patients to providers, and calculates the compliance rate against a versioned measure library aligned to HEDIS and CMS specifications.</p>
            <p>Results include the full breakdown: eligible patients, excluded patients with reasons, compliant patients, compliance rate, tier achieved, and dollars earned — down to the individual patient level.</p>
            <div className="step-detail">Standardized measure library with 12+ pre-built definitions covering diabetes, readmissions, screenings, blood pressure, depression, BMI, immunizations, and medication adherence.</div>
          </div>
          <div className="step-visual sv-purple">
            <div className="vis-title">Measure library (HEDIS-aligned)</div>
            <div className="measure-grid">
              {[['Diabetes control', 'HbA1c < 7.0%', 'HEDIS CDC'],
                ['30-day readmissions', 'All-cause readmit rate', 'HEDIS PCR'],
                ['BP control', '< 140/90 mmHg', 'HEDIS CBP'],
                ['Breast cancer screen', 'Mammogram completion', 'HEDIS BCS'],
                ['Depression screening', 'PHQ-9 + follow-up', 'HEDIS DSF'],
                ['Colorectal screening', 'Colonoscopy / FIT', 'HEDIS COL'],
                ['Med adherence', 'PDC ≥ 80%', 'HEDIS SAA'],
                ['Custom measures', 'Client-defined', 'CUSTOM']
              ].map(([name, desc, badge]) => (
                <div key={name} className="mg">
                  <div className="mg-name">{name}</div>
                  <div className="mg-desc">{desc}</div>
                  <span className={`mg-badge${badge === 'CUSTOM' ? ' mg-custom' : ''}`}>{badge}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* STEP 4 */}
        <div className="step reveal">
          <div className="step-visual sv-red" style={{ order: -1 }}>
            <div className="vis-title">Care gap detection</div>
            <div className="gap-rows">
              {[['Maria Santos', 'HbA1c 8.9%', 'HIGH', '$600'],
                ['James Chen', 'HbA1c 8.4%', 'HIGH', '$600'],
                ['Priya Patel', 'Readmit 03/12', 'HIGH', '$12,400'],
                ['Robert Kim', 'HbA1c 7.8%', 'MED', '$600'],
                ['Lisa Jackson', 'HbA1c 7.6%', 'MED', '$600']
              ].map(([name, val, tag, rev]) => (
                <div key={name} className="gr">
                  <span className="gr-name">{name}</span>
                  <span className="gr-val">{val}</span>
                  <span className={`gr-tag ${tag === 'HIGH' ? 'gt-high' : 'gt-med'}`}>{tag}</span>
                  <span className="gr-rev">{rev}</span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 14, padding: '12px 14px', background: 'rgba(201,68,68,0.04)', borderRadius: 8, border: '1px solid rgba(201,68,68,0.14)' }}>
              <div style={{ fontSize: 9, color: 'var(--cy-red)', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>Recoverable revenue</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--cy-gold)', letterSpacing: -1 }}>$74,400</div>
              <div style={{ fontSize: 10, color: 'var(--cy-txm)' }}>69 patients with addressable care gaps</div>
            </div>
          </div>
          <div className="step-left">
            <div className="step-num">04</div>
            <div className="step-tag">Step 4 — Act</div>
            <h3>Close the gaps that cost you money</h3>
            <p>Every patient who falls short of a quality threshold is a care gap — and a revenue gap. CareYield identifies these patients, prioritizes them by revenue impact, and routes them as actionable cases to your care coordination team.</p>
            <p>Each care gap includes clinical context: the patient's trend, comorbidities, current medications, and a recommended action. Coordinators see exactly what needs to happen and why it matters financially.</p>
            <div className="step-detail">Cases carry SLAs: 7 days for high-urgency gaps, 14 for medium, 30 for low. When a coordinator closes a case, the patient is flagged for recalculation in the next cycle.</div>
          </div>
        </div>

        {/* STEP 5 */}
        <div className="step reveal">
          <div className="step-left">
            <div className="step-num">05</div>
            <div className="step-tag">Step 5 — Collect</div>
            <h3>Know exactly what you're owed</h3>
            <p>CareYield translates clinical performance into dollars — applying every contract rule: tiered payouts, shared savings formulas, risk adjustment, caps, and minimum sample sizes. The result is an audit-grade payout report with per-measure, per-tier detail and full reproducibility.</p>
            <p>A built-in approval workflow routes calculations through clinical review, finance validation, and payer submission. If a payer disputes, every calculation can be reproduced against its frozen data snapshot.</p>
            <div className="step-detail">Compare performance across all payers in a single view. Heatmaps show where you're strong and where you're leaving money — by payer, by measure, by provider.</div>
          </div>
          <div className="step-visual sv-amber">
            <div className="vis-title">Payout reconciliation</div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--cy-txm)', marginBottom: 4 }}>
                <span>Diabetes Control — Tier 1</span>
                <span style={{ color: 'var(--cy-teal)' }}>77.9% (target: 75%)</span>
              </div>
              <div className="payout-bar"><div className="pb-fill pb-earned" style={{ width: '100%' }}>$15,600</div></div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--cy-txm)', marginBottom: 4 }}>
                <span>30-Day Readmissions — Shared Savings</span>
                <span style={{ color: 'var(--cy-teal)' }}>12.0% (&lt;15%)</span>
              </div>
              <div className="payout-bar"><div className="pb-fill pb-earned" style={{ width: '85%' }}>$420,000</div></div>
              <div className="payout-label"><span></span><span style={{ color: 'var(--cy-gold)' }}>$74,400 recoverable</span></div>
            </div>
            <div className="payout-total">
              <span className="pt-label">Total earned this period</span>
              <span className="pt-val">$435,600</span>
            </div>
          </div>
        </div>
      </div>

      {/* ARCHITECTURE */}
      <section className="arch-deep reveal">
        <span className="section-label">Architecture</span>
        <h2 className="section-h2">Three layers, one <span>truth</span>.</h2>
        <p style={{ fontSize: 14, color: 'var(--cy-txm)', maxWidth: 520, lineHeight: 1.72, marginBottom: 0 }}>
          CareYield is built on a three-layer architecture that separates intelligence, data, and operations. Each layer is purpose-built and communicates through clean APIs.
        </p>
        <div className="arch-cols">
          <div className="arch-col ac-1">
            <div className="arch-col-title">Intelligence</div>
            <div className="arch-col-sub">AI-powered reasoning</div>
            <p>Reads contracts, understands clinical measures, calculates performance, detects care gaps, generates recommendations.</p>
            {['Contract parsing and validation', 'Versioned measure library (HEDIS/CMS)', 'Patient attribution engine', 'Care gap detection and prioritization', 'ROI modeling and projections', 'Full audit trail with reproducibility'].map(a => <div key={a} className="ai">{a}</div>)}
          </div>
          <div className="arch-col ac-2">
            <div className="arch-col-title">Data</div>
            <div className="arch-col-sub">Enterprise analytics</div>
            <p>Handles large-scale patient data processing, normalization, identity matching, and high-performance measure calculations.</p>
            {['FHIR R4 EMR integration', 'Probabilistic identity matching', 'Data normalization and quality rules', 'Cohort analytics and time-windowed queries', 'Row-level security and PHI masking', 'Frozen snapshots for audit reproducibility'].map(a => <div key={a} className="ai">{a}</div>)}
          </div>
          <div className="arch-col ac-3">
            <div className="arch-col-title">Operations</div>
            <div className="arch-col-sub">Enterprise workflow</div>
            <p>Dashboards, care coordination workflows, approval chains, payer reconciliation, and the tooling that turns insights into action.</p>
            {['Executive and operational dashboards', 'Care coordination case management', 'Multi-step approval workflows', 'Payer submission and dispute tracking', 'Cross-payer performance comparison', 'Role-based access control (5 roles)'].map(a => <div key={a} className="ai">{a}</div>)}
          </div>
        </div>
      </section>

      {/* COMPLIANCE */}
      <section className="compliance-sec reveal">
        <span className="section-label">Compliance</span>
        <h2 className="section-h2">Built for <span>healthcare</span></h2>
        <div className="comp-grid">
          <div className="comp-card">
            <div className="comp-icon"><Shield size={20} /></div>
            <h4>HIPAA compliant</h4>
            <p>End-to-end encryption (AES-256 at rest, TLS 1.3 in transit), BAA coverage, automated breach detection.</p>
          </div>
          <div className="comp-card">
            <div className="comp-icon"><Users size={20} /></div>
            <h4>Role-based access</h4>
            <p>Five configurable roles. PHI dynamically masked based on role — finance sees de-identified data by default.</p>
          </div>
          <div className="comp-card">
            <div className="comp-icon"><FileText size={20} /></div>
            <h4>Audit-grade logging</h4>
            <p>Every calculation, data access, and approval logged with timestamps, actors, and SHA-256 integrity hashes.</p>
          </div>
          <div className="comp-card">
            <div className="comp-icon"><Activity size={20} /></div>
            <h4>Healthcare standards</h4>
            <p>FHIR R4, HEDIS/CMS alignment, ICD-10/LOINC/RxNorm/CPT validation, NQF measure numbering.</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <div className="hiw-cta">
        <span className="section-label">Ready?</span>
        <h2 className="section-h2" style={{ fontSize: 40, textAlign: 'center' }}>See it <span>live</span>.</h2>
        <p style={{ fontSize: 14, color: 'var(--cy-txm)', maxWidth: 420, margin: '0 auto 28px', lineHeight: 1.72, textAlign: 'center' }}>
          Walk through the full platform with your own contract — from upload to payout in under 60 seconds.
        </p>
        <Link to="/contact" className="btn-primary">
          Request a demo
          <ArrowRight size={15} strokeWidth={2.5} />
        </Link>
      </div>
    </div>
  );
};

export default HowItWorks;
